import { Alert } from "antd";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { ExecuteReportValues, AdaptedValues } from "shared/types";
import * as Yup from "yup";
import { ReportItem } from "shared/api";
import { MaterialItemFilter } from "./MaterialItemFilter";
import { PackagingFilter } from "./PackagingFilter";
import { MultiplePackagingFilter } from "./MultiplePackagingFilter";
import { OrderTypeFilter } from "./OrderTypeFilter";
import { MultipleOrderFiltering } from "./MultipleOrderFiltering";
import { ExportReportHeader } from "./ExportReportHeader";
import { IsReelLabFilters } from "./IsReelLabFilters";
import { MultipleQualityFiltering } from "./MultipleQualityFiltering";
import { MultipleItemFilter } from "./MultipleItemFilter";
import { MultipleOrderDaily } from "./MultipleOrderDaily";
import { DriggingRiggFilter } from "./DriggingRiggFilter";
import { StockFilter } from "./StockReport";
import { MultiOrdersPackagesFilter } from "./MultiOrdersPackagingFilter";
import { CompanyOrdersPackagesFilter } from "./CompanyPackagingFilter";
import { QualityAdhesionFilter } from "./QualityAdhesionFilter";
import { ReelLabResultMultipleFilter } from "./ReelLabResultMultipleFilter";
import { ConsumptionFilter } from "./ConsumptionFilter";

export interface ExecuteReports {
  handleExecuteReport: () => Promise<void>;
  isLoadingReportExport: boolean;
}

export interface ReportsData {
  handleGetReportData: () => Promise<void>;
  isLoadingReportData: boolean;
}

interface ExecuteReportFormProps {
  isNotSupportedYetType: boolean;
  isMaterialItem: boolean;
  adaptedOrders: AdaptedValues[];
  onOrderSearch: (value: string) => void;
  selectedOrderId: number | null;
  onOrderChange: (value: number | null) => void;
  isPackaging: boolean;
  isMultiplePackaging: boolean;
  isOrderType: boolean;
  isMultipleOrderType: boolean;
  executeReports: ExecuteReports;
  isValid: boolean;
  validationSchema?: Yup.AnyObjectSchema;
  getReportData: ReportsData;
  selectedReport?: ReportItem;
  pdfUrl: string | null;
  clearPdfUrl: () => void;
  isReelLabResultType: boolean;
  selectedMaterialId: number | null;
  onMaterialChange: (value: number | null) => void;
  isReelLabResultMultipleType: boolean;
  selectedCompanyId: number | null;
  onCompanyChange: (value: number | null) => void;
  selectedProductCardIds: number[];
  onProductCardChange: (value: number[]) => void;
  isMultipleQualityType: boolean;
  isMultipleItemType: boolean;
  isMultipleOrderDaily: boolean;
  isDriggingRigg: boolean;
  isLoadingOrders: boolean;
  isStockType: boolean;
  isMultipleOrdersAndPackaging: boolean;
  isMultipleCompanyPackaging: boolean;
  isQualityAdhesion: boolean;
  isConsumption: boolean;
}

const ExecuteReportForm: FC<ExecuteReportFormProps> = ({
  isMaterialItem,
  isNotSupportedYetType,
  adaptedOrders,
  onOrderSearch,
  selectedOrderId,
  onOrderChange,
  isPackaging,
  isMultiplePackaging,
  isOrderType,
  isMultipleOrderType,
  executeReports,
  isValid,
  validationSchema,
  getReportData,
  selectedReport,
  pdfUrl,
  clearPdfUrl,
  isReelLabResultType,
  selectedMaterialId,
  onMaterialChange,
  isReelLabResultMultipleType,
  selectedCompanyId,
  onCompanyChange,
  selectedProductCardIds,
  onProductCardChange,
  isMultipleQualityType,
  isMultipleItemType,
  isMultipleOrderDaily,
  isDriggingRigg,
  isLoadingOrders,
  isStockType,
  isMultipleOrdersAndPackaging,
  isMultipleCompanyPackaging,
  isQualityAdhesion,
  isConsumption,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values } = useFormikContext<ExecuteReportValues>();

  if (!values?.id) {
    return (
      <Alert
        showIcon
        type="warning"
        message={t("selectReport")}
        className="h-10 w-full"
      />
    );
  }

  return (
    <div className="w-full flex flex-col">
      <ExportReportHeader
        pdfUrl={pdfUrl}
        selectedReport={selectedReport}
        executeReports={executeReports}
        isValid={isValid}
        validationSchema={validationSchema}
        getReportData={getReportData}
        clearPdfUrl={clearPdfUrl}
      />

      {isNotSupportedYetType && (
        <Alert
          type="warning"
          showIcon
          className="mt-2"
          message={t("reportTypeIsNotSupportedYet")}
        />
      )}

      {isMaterialItem && (
        <MaterialItemFilter
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
        />
      )}

      {isPackaging && (
        <PackagingFilter
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
        />
      )}

      {isMultiplePackaging && (
        <MultiplePackagingFilter
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
        />
      )}

      {isOrderType && (
        <OrderTypeFilter
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
        />
      )}

      {isMultipleOrderType && (
        <MultipleOrderFiltering selectedReport={selectedReport} />
      )}

      {isReelLabResultType && (
        <IsReelLabFilters
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
          selectedMaterialId={selectedMaterialId}
          onMaterialChange={onMaterialChange}
        />
      )}

      {isReelLabResultMultipleType && (
        <ReelLabResultMultipleFilter
          selectedCompanyId={selectedCompanyId}
          onCompanyChange={onCompanyChange}
          selectedProductCardIds={selectedProductCardIds}
          onProductCardChange={onProductCardChange}
          clearPdfUrl={clearPdfUrl}
          selectedReport={selectedReport}
        />
      )}

      {isMultipleQualityType && (
        <MultipleQualityFiltering
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
          clearPdfUrl={clearPdfUrl}
          selectedReport={selectedReport}
        />
      )}

      {isMultipleItemType && (
        <MultipleItemFilter
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
          clearPdfUrl={clearPdfUrl}
          selectedReport={selectedReport}
        />
      )}

      {isMultipleOrderDaily && (
        <MultipleOrderDaily selectedReport={selectedReport} />
      )}

      {isDriggingRigg && <DriggingRiggFilter />}

      {isStockType && <StockFilter />}

      {isMultipleOrdersAndPackaging && (
        <MultiOrdersPackagesFilter
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
        />
      )}

      {isMultipleCompanyPackaging && <CompanyOrdersPackagesFilter />}

      {isQualityAdhesion && (
        <QualityAdhesionFilter
          isLoadingOrders={isLoadingOrders}
          adaptedOrders={adaptedOrders}
          onOrderSearch={onOrderSearch}
          selectedOrderId={selectedOrderId}
          onOrderChange={onOrderChange}
          clearPdfUrl={clearPdfUrl}
          selectedReport={selectedReport}
        />
      )}

      {isConsumption && (
        <ConsumptionFilter
          clearPdfUrl={clearPdfUrl}
          selectedReport={selectedReport}
        />
      )}
    </div>
  );
};

export { ExecuteReportForm };
